import React, { useState, useEffect } from 'react';
import {
  Button,
  Text,
  Collapse,
  Select,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  Box,
  Divider,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useColors } from './colors';

const StoreMapping = ({
  shipStationStores = [],
  storeOptions = [],
  getStoresListStatus = 'idle',
  mappedStores = [],
  onSave,
  updateStatus = 'idle',
}) => {
  const [isMapStoreOpen, setIsMapStoreOpen] = useState(false);
  const [selectedStores, setSelectedStores] = useState({});
  const [savingStoreId, setSavingStoreId] = useState(null);

  const {
    borderColor,
    textColor,
    inputBackground,
    inputColor,
    filterBg,
    filterOptionHover,
    filterOptionChecked,
    filterHover,
    filterChecked,
  } = useColors();

  useEffect(() => {
    if (mappedStores.length > 0) {
      const initialMapping = {};
      mappedStores.forEach((mapping) => {
        if (mapping.shipStationStoreId && mapping.storeId) {
          initialMapping[mapping.shipStationStoreId] = mapping.storeId;
        }
      });
      setSelectedStores(initialMapping);
    }
  }, [mappedStores]);

  const handleStoreSelection = (shipStationStoreId, storeId) => {
    setSelectedStores((prev) => ({
      ...prev,
      [shipStationStoreId]: storeId,
    }));
  };

  const handleSaveMapping = (shipStationStoreId) => {
    const selectedStoreId = selectedStores[shipStationStoreId];

    if (!selectedStoreId || !onSave) return;

    setSavingStoreId(shipStationStoreId);

    const newMapping = {
      shipStationStoreId: parseInt(shipStationStoreId, 10),
      storeId: selectedStoreId,
    };

    const updatedMappedStores = mappedStores.filter(
      (mapping) => mapping.shipStationStoreId !== parseInt(shipStationStoreId, 10),
    );
    updatedMappedStores.push(newMapping);

    onSave(updatedMappedStores).finally(() => {
      setSavingStoreId(null);
    });
  };

  const getMappedStoreLabel = (shipStationStoreId) => {
    const mappedStoreId = selectedStores[shipStationStoreId];
    if (!mappedStoreId) return null;

    const store = storeOptions.find((s) => s.value === mappedStoreId);
    return store ? store.label : mappedStoreId;
  };

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="hidden"
      bg="white"
      _dark={{ bg: 'navy.800' }}
    >
      <Button
        colorScheme="purple"
        size="md"
        width="100%"
        borderRadius="none"
        justifyContent="space-between"
        onClick={() => setIsMapStoreOpen(!isMapStoreOpen)}
        rightIcon={isMapStoreOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
      >
        Map store
      </Button>

      <Collapse in={isMapStoreOpen} animateOpacity>
        <Box p={4} bg={inputBackground}>
          <VStack spacing={3} align="stretch">
            {shipStationStores.length === 0 ? (
              <Text fontSize="sm" color="gray.500" py={2}>
                No ShipStation stores found. Save API credentials first.
              </Text>
            ) : (
              shipStationStores.map((shipStore) => {
                const isCurrentlySaving = savingStoreId === shipStore.storeId;
                const mappedStoreLabel = getMappedStoreLabel(shipStore.storeId);

                return (
                  <Box
                    key={shipStore.storeId}
                    p={4}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                    bg="white"
                    _dark={{ bg: 'navy.800' }}
                  >
                    <HStack
                      spacing={4}
                      align={{ base: 'stretch', md: 'center' }}
                      flexDirection={{ base: 'column', md: 'row' }}
                    >
                      <Box flex="1" minW={0}>
                        <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                          {shipStore.storeName}
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                          ID {shipStore.storeId} · {shipStore.marketplaceName}
                        </Text>
                        {mappedStoreLabel && (
                          <Text fontSize="xs" color="green.500" mt={1}>
                            Mapped to {mappedStoreLabel}
                          </Text>
                        )}
                      </Box>

                      <Divider
                        display={{ base: 'none', md: 'block' }}
                        orientation="vertical"
                        height="48px"
                        borderColor={borderColor}
                      />

                      <FormControl flex={{ md: '1' }} maxW={{ md: '280px' }}>
                        <FormLabel fontSize="xs" color={textColor} mb={1}>
                          Marketplace store
                        </FormLabel>
                        <Select
                          placeholder="Select store"
                          size="sm"
                          value={selectedStores[shipStore.storeId] || ''}
                          onChange={(e) =>
                            handleStoreSelection(shipStore.storeId, e.target.value)
                          }
                          isDisabled={
                            getStoresListStatus === 'pending' || isCurrentlySaving
                          }
                          sx={{
                            option: {
                              bg: filterBg,
                              color: inputColor,
                            },
                            '> option:hover': {
                              bg: `${filterOptionHover} !important`,
                            },
                            '> option:checked': {
                              bg: `${filterOptionChecked} !important`,
                            },
                          }}
                          _hover={{ bg: filterHover }}
                          _focus={{
                            bg: filterChecked,
                            borderColor: 'blue.300',
                          }}
                          color={textColor}
                        >
                          {storeOptions?.map((store) => (
                            <option key={store.id} value={store.value}>
                              {store.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      <Button
                        colorScheme="green"
                        size="sm"
                        alignSelf={{ base: 'stretch', md: 'flex-end' }}
                        minW={{ md: '88px' }}
                        onClick={() => handleSaveMapping(shipStore.storeId)}
                        isLoading={isCurrentlySaving}
                        loadingText="Saving…"
                        isDisabled={
                          !selectedStores[shipStore.storeId] ||
                          updateStatus === 'pending'
                        }
                      >
                        Save
                      </Button>
                    </HStack>
                  </Box>
                );
              })
            )}
          </VStack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default StoreMapping;
